import React, { useEffect, useState } from 'react';
import { XIcon, TrendingUpIcon } from 'lucide-react';
import { ExchangeRate, RateHistory } from '../CurrencyExchangeView';
import { ExchangeRateBadge } from './ExchangeRateBadge';
interface RateHistoryViewProps {
  rate: ExchangeRate;
  onClose: () => void;
  locale: string;
}
export function RateHistoryView({
  rate,
  onClose,
  locale
}: RateHistoryViewProps) {
  const [history, setHistory] = useState<RateHistory[]>([]);
  const [loading, setLoading] = useState(true);
  // Load sample history data
  useEffect(() => {
    // In a real app, this would be an API call to fetch history for the specific rate
    setLoading(true);
    // Generate sample history data
    const generateHistory = () => {
      const now = new Date();
      const sampleHistory: RateHistory[] = [];
      // Generate 30 days of history
      for (let i = 0; i < 30; i++) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        // Add some random variation to the rate
        const variation = Math.random() * 0.1 - 0.05; // -5% to +5%
        const historicRate = rate.rate * (1 + variation);
        sampleHistory.push({
          id: `history-${rate.id}-${i}`,
          rateId: rate.id,
          fromCurrency: rate.fromCurrency,
          toCurrency: rate.toCurrency,
          rate: historicRate,
          date: date.toISOString(),
          type: i % 7 === 0 ? 'Manual' : rate.type,
          source: i % 7 === 0 ? 'User Update' : rate.updatedBy || 'System'
        });
      }
      return sampleHistory.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    };
    // Simulate API delay
    setTimeout(() => {
      setHistory(generateHistory());
      setLoading(false);
    }, 500);
  }, [rate]);
  // Format date based on locale
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-10194">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-10195">
        <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-10196">
          <TrendingUpIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10197" />
          Rate History
        </h3>
        <button onClick={onClose} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10198">
          <XIcon className="h-5 w-5" data-id="element-10199" />
        </button>
      </div>
      <div className="p-4" data-id="element-10200">
        <div className="mb-4 p-4 bg-gray-50 rounded-lg" data-id="element-10201">
          <div className="flex items-center justify-center space-x-2 text-lg font-medium" data-id="element-10202">
            <span data-id="element-10203">{rate.fromCurrency}</span>
            <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-10204" />
            <span data-id="element-10205">{rate.toCurrency}</span>
          </div>
          <div className="mt-2 text-center" data-id="element-10206">
            <div className="text-sm text-gray-500" data-id="element-10207">Current Rate</div>
            <div className="text-2xl font-semibold" data-id="element-10208">{rate.rate.toFixed(4)}</div>
          </div>
        </div>
        {/* Rate history chart would go here in a real implementation */}
        <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center mb-4" data-id="element-10209">
          <span className="text-gray-500 text-sm" data-id="element-10210">
            Rate history chart would be displayed here
          </span>
        </div>
        {/* History table */}
        <div className="overflow-x-auto mt-4" data-id="element-10211">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-10212">
            <thead className="bg-gray-50" data-id="element-10213">
              <tr data-id="element-10214">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10215">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10216">
                  Rate
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10217">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10218">
                  Source
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-10219">
              {loading ? <tr data-id="element-10220">
                  <td colSpan={4} className="px-6 py-4 text-center" data-id="element-10221">
                    <div className="flex justify-center" data-id="element-10222">
                      <svg className="animate-spin h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-10223">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-10224"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-10225"></path>
                      </svg>
                    </div>
                  </td>
                </tr> : history.map(item => <tr key={item.id} data-id="element-10226">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10227">
                      {formatDate(item.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-10228">
                      {item.rate.toFixed(4)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-10229">
                      <ExchangeRateBadge type={item.type} data-id="element-10230" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10231">
                      {item.source}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
// Helper component for the arrow icon
const ArrowRightIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-10232">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-10233" />
  </svg>;